import React from 'react';
import { TableRow, TableCell } from './table';
import { Button } from './button';
import { Badge } from './badge';
import { Checkbox } from './checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { 
  MoreHorizontal, Eye, Trash2, UserCircle, 
  MessageCircle, Calendar, Mail, FileText, History,
  MessageSquare, ClipboardCheck, Phone, Link2, User, Users, Bell, PenTool,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';

// Get flag emoji for country name
const getCountryFlag = (countryName) => {
  if (!countryName) return '🌍';
  const name = countryName.toLowerCase().trim();
  
  // Common countries mapping
  if (name === 'pakistan') return '🇵🇰';
  if (name === 'india') return '🇮🇳';
  if (name === 'nigeria') return '🇳🇬';
  if (name === 'bangladesh') return '🇧🇩';
  if (name === 'nepal') return '🇳🇵';
  if (name === 'sri lanka') return '🇱🇰';
  if (name === 'china') return '🇨🇳';
  if (name === 'vietnam') return '🇻🇳';
  if (name === 'philippines') return '🇵🇭';
  if (name === 'indonesia') return '🇮🇩';
  if (name === 'malaysia') return '🇲🇾';
  if (name === 'thailand') return '🇹🇭';
  if (name === 'united states' || name === 'usa') return '🇺🇸';
  if (name === 'united kingdom' || name === 'uk') return '🇬🇧';
  if (name === 'canada') return '🇨🇦';
  if (name === 'australia') return '🇦🇺';
  if (name === 'germany') return '🇩🇪';
  if (name === 'france') return '🇫🇷';
  if (name === 'italy') return '🇮🇹';
  if (name === 'spain') return '🇪🇸';
  if (name === 'netherlands') return '🇳🇱';
  if (name === 'belgium') return '🇧🇪';
  if (name === 'sweden') return '🇸🇪';
  if (name === 'norway') return '🇳🇴';
  if (name === 'denmark') return '🇩🇰';
  if (name === 'finland') return '🇫🇮';
  if (name === 'ireland') return '🇮🇪';
  if (name === 'switzerland') return '🇨🇭';
  if (name === 'austria') return '🇦🇹';
  if (name === 'poland') return '🇵🇱';
  if (name === 'russia') return '🇷🇺';
  if (name === 'ukraine') return '🇺🇦';
  if (name === 'turkey') return '🇹🇷';
  if (name === 'saudi arabia') return '🇸🇦';
  if (name === 'uae' || name === 'united arab emirates') return '🇦🇪';
  if (name === 'qatar') return '🇶🇦';
  if (name === 'kuwait') return '🇰🇼';
  if (name === 'oman') return '🇴🇲';
  if (name === 'bahrain') return '🇧🇭';
  if (name === 'jordan') return '🇯🇴';
  if (name === 'egypt') return '🇪🇬';
  if (name === 'south africa') return '🇿🇦';
  if (name === 'kenya') return '🇰🇪';
  if (name === 'ghana') return '🇬🇭';
  if (name === 'ethiopia') return '🇪🇹';
  if (name === 'morocco') return '🇲🇦';
  if (name === 'brazil') return '🇧🇷';
  if (name === 'mexico') return '🇲🇽';
  if (name === 'argentina') return '🇦🇷';
  if (name === 'japan') return '🇯🇵';
  if (name === 'south korea' || name === 'korea') return '🇰🇷';
  if (name === 'singapore') return '🇸🇬';
  if (name === 'new zealand') return '🇳🇿';
  if (name === 'iran') return '🇮🇷';
  if (name === 'iraq') return '🇮🇶';
  if (name === 'afghanistan') return '🇦🇫';
  
  return '🌍'; // Default globe for unknown countries
};

// WhatsApp Icon SVG
const WhatsAppIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Application Row Component - Separated to avoid babel plugin issues
export const ApplicationTableRow = ({ 
  app, 
  isColumnVisible, 
  selectedApps, 
  toggleSelectApp, 
  navigate, 
  openAssignDialog, 
  openDocShortfall, 
  fetchDeskHistory,
  openChat,
  openAgentChat,
  openReminder,
  openActivityLog,
  openEmailCompose,
  openSMSCompose,
  openWhatsAppCompose,
  openContentWriter,
  openVettingDialog,
  openDuplicateDialog,
  handleDelete,
  unreadCount,
  reminderStats = { sent: 0, pending: 0 },
  getDeskColor,
  getStatusColor,
  isUnread = false,
  isAdmin = false,  // New prop to control admin-only features
  onMarkAsRead,
  currentTab = 'fresh_leads',  // Tab to preserve on back navigation
  currentPage = 1,  // Page to preserve on back navigation
  currentLimit = 20,  // Records per page to preserve on back navigation
  duplicateInfo = null  // Info about duplicate applications
}) => {
  const createdDate = new Date(app.created_at);
  const today = new Date();
  const diffTime = Math.abs(today - createdDate);
  const appAgeDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  // Calculate Offer Age from date_of_offer in desk_data.admission
  const dateOfOffer = app.desk_data?.admission?.date_of_offer;
  let offerAgeDays = null;
  if (dateOfOffer) {
    const offerDate = new Date(dateOfOffer);
    const offerDiffTime = Math.abs(today - offerDate);
    offerAgeDays = Math.floor(offerDiffTime / (1000 * 60 * 60 * 24));
  }
  
  const appType = app.desk_data?.counselor?.application_type || 'direct';
  const getAppTypeDisplay = (type) => {
    const normalizedType = type?.toLowerCase()?.replace(/\s+/g, '_') || 'direct';
    switch(normalizedType) {
      case 'web_form': return 'Web Form';
      case 'student_portal': return 'Student Portal';
      default: return type?.charAt(0)?.toUpperCase() + type?.slice(1) || 'Direct';
    }
  };
  const getAppTypeBadgeClass = (type) => {
    const normalizedType = type?.toLowerCase()?.replace(/\s+/g, '_') || 'direct';
    switch(normalizedType) {
      case 'agent': return 'border-orange-500 text-orange-600';
      case 'office': return 'border-blue-500 text-blue-600';
      case 'web_form': return 'border-amber-500 text-amber-500';
      case 'student_portal': return 'border-amber-500 text-amber-500';
      default: return 'border-green-500 text-green-600';
    }
  };
  const docCount = app.documents_count || 0;
  
  // Get country from multiple possible sources
  const getAppCountry = () => {
    // Check direct country field first
    if (app.country) return app.country;
    // Then check desk_data.counselor.country_living
    if (app.desk_data?.counselor?.country_living) return app.desk_data.counselor.country_living;
    // Check nationality as fallback
    if (app.desk_data?.counselor?.nationality) return app.desk_data.counselor.nationality;
    return null;
  };
  
  const appCountry = getAppCountry();
  
  // Handle row click - mark as read
  const handleRowClick = () => {
    if (onMarkAsRead && isUnread) {
      onMarkAsRead(app.id);
    }
  };

  // Check if this application has high reminders (3+)
  const hasHighReminders = (app.reminder_stats?.total || 0) >= 3;
  
  // Check if this is a duplicate application
  const isDuplicate = duplicateInfo !== null;

  return (
    <TableRow 
      className={`cursor-pointer transition-all duration-200 ${
        isDuplicate
          ? 'duplicate-row'
          : hasHighReminders
            ? 'high-reminders-row'
            : isUnread 
              ? 'unread-row' 
              : 'hover:bg-muted/50'
      }`}
      onClick={handleRowClick}
      data-testid={`app-row-${app.id}`}
    >
      {isColumnVisible('checkbox') && (
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selectedApps.includes(app.id)}
            onCheckedChange={() => toggleSelectApp(app.id)}
            data-testid={`select-app-${app.id}`}
          />
        </TableCell>
      )}
      {isColumnVisible('app_number') && (
        <TableCell
          className="font-mono text-xs font-semibold text-slate-800 dark:text-amber-300"
          onClick={() => { handleRowClick(); navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`); }}
          data-testid={`app-number-${app.id}`}
        >
          {app.app_number ? `#${app.app_number}` : '-'}
        </TableCell>
      )}
      {isColumnVisible('student_name') && (
        <TableCell className="font-medium" onClick={() => { handleRowClick(); navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`); }}>
          <div className="flex flex-col">
            {isDuplicate && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-[9px] font-bold text-orange-600 bg-orange-100 px-1 py-0.5 rounded w-fit mb-0.5 cursor-help">DUP</span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-sm">
                    <p className="font-medium text-white">Duplicate Detected</p>
                    <p className="text-xs text-white/80 mt-1">
                      {duplicateInfo?.reasons?.map(r => {
                        if (r.startsWith('passport:')) return `Same passport: ${r.split(':')[1]}`;
                        if (r === 'name+phone') return 'Same name + phone';
                        if (r === 'name+university') return 'Same name + university';
                        return r;
                      }).join(', ')}
                    </p>
                    <p className="text-xs text-white mt-1">{duplicateInfo?.count} duplicate(s) found</p>
                    {duplicateInfo?.duplicate_owners?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-white/30">
                        <p className="text-xs font-medium text-white mb-1">Duplicates held by:</p>
                        {duplicateInfo.duplicate_owners.slice(0, 5).map((owner, idx) => (
                          <p key={idx} className="text-xs text-white/90">
                            • {owner.case_owner_name || 'Unassigned'} - {owner.student_name}
                          </p>
                        ))}
                        {duplicateInfo.duplicate_owners.length > 5 && (
                          <p className="text-xs text-white/70 italic">
                            ... and {duplicateInfo.duplicate_owners.length - 5} more
                          </p>
                        )}
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <div className="flex items-center gap-1">
              {hasHighReminders && !isDuplicate && (
                <span className="reminder-warning-dot" title="3+ reminders sent" />
              )}
              {isUnread && !hasHighReminders && !isDuplicate && (
                <span className="unread-dot" />
              )}
              <span>{app.student_name}</span>
              {app.is_agent_submission && (
                <Badge variant="outline" className="text-[10px] h-4 px-1">Agent</Badge>
              )}
            </div>
          </div>
        </TableCell>
      )}
      {isColumnVisible('student_email') && (
        <TableCell onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`)}>{app.student_email}</TableCell>
      )}
      {isColumnVisible('student_phone') && (
        <TableCell onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`)}>{app.student_phone || '-'}</TableCell>
      )}
      {isColumnVisible('country') && (
        <TableCell onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`)}>
          {appCountry ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-2xl cursor-pointer hover:scale-110 transition-transform inline-block">
                    {getCountryFlag(appCountry)}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-medium">{appCountry}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
      )}
      {isColumnVisible('partner_university') && (
        <TableCell onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`)}>
          {app.university_name ? (
            <span className="text-sm font-medium">{app.university_name}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
      )}
      {isColumnVisible('app_type') && (
        <TableCell onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`)}>
          <div className="flex flex-col gap-1">
            <Badge variant="outline" className={`text-xs ${getAppTypeBadgeClass(appType)}`}>
              {getAppTypeDisplay(appType)}
            </Badge>
            {(appType?.toLowerCase() === 'agent') && app.agent_name && (
              <span className="text-xs text-muted-foreground truncate max-w-[100px]" title={app.agent_name}>
                {app.agent_name}
              </span>
            )}
          </div>
        </TableCell>
      )}
      {isColumnVisible('assigned_to') && (
        <TableCell onClick={(e) => e.stopPropagation()}>
          {isAdmin ? (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-2 text-xs"
              onClick={() => openAssignDialog(app)}
              data-testid={`assign-btn-${app.id}`}
            >
              <UserCircle className="w-3 h-3 mr-1" />
              {app.assigned_to_name || 'Unassigned'}
            </Button>
          ) : (
            <span className="text-sm flex items-center gap-1">
              <UserCircle className="w-3 h-3" />
              {app.assigned_to_name || 'Unassigned'}
            </span>
          )}
        </TableCell>
      )}
      {isColumnVisible('current_desk') && (
        <TableCell onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`)}>
          <div className="flex flex-col gap-0.5">
            <Badge className={`${getDeskColor(app.current_desk)} border w-fit`}>
              {app.current_desk}
            </Badge>
            {app.current_desk_handler_name && (
              <span className="text-[10px] text-muted-foreground">
                → {app.current_desk_handler_name}
              </span>
            )}
          </div>
        </TableCell>
      )}
      {isColumnVisible('status') && (
        <TableCell onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`)}>
          <Badge className={getStatusColor(app.status)}>
            {app.status}
          </Badge>
        </TableCell>
      )}
      {isColumnVisible('documents') && (
        <TableCell onClick={(e) => openDocShortfall(app, e)} className="cursor-pointer">
          <Badge 
            variant="outline" 
            className={`gap-1 ${docCount >= (app.required_docs_count || 0) && app.required_docs_count > 0 ? 'border-green-500 text-green-600' : docCount < (app.required_docs_count || 0) ? 'border-amber-500 text-amber-600' : ''}`}
          >
            <FileText className="w-3 h-3" />
            {docCount}/{app.required_docs_count ?? '-'}
          </Badge>
        </TableCell>
      )}
      {isColumnVisible('reminders') && (
        <TableCell>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge 
                  variant="outline" 
                  className={`gap-1 cursor-help ${(app.reminder_stats?.total || 0) > 0 ? 'border-blue-500 text-blue-600' : 'border-gray-300 text-gray-500'}`}
                >
                  <Bell className="w-3 h-3" />
                  {app.reminder_stats?.total || 0}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="w-64">
                <div className="text-sm space-y-2">
                  <p className="font-semibold border-b pb-1">Communication Summary</p>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> Emails:
                      </span>
                      <span className="font-medium">{app.reminder_stats?.email?.count || 0}</span>
                    </div>
                    {app.reminder_stats?.email?.dates?.length > 0 && (
                      <div className="text-xs text-muted-foreground pl-4">
                        Last: {new Date(app.reminder_stats.email.dates[app.reminder_stats.email.dates.length - 1]).toLocaleDateString()}
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" /> WhatsApp:
                      </span>
                      <span className="font-medium">{app.reminder_stats?.whatsapp?.count || 0}</span>
                    </div>
                    {app.reminder_stats?.whatsapp?.dates?.length > 0 && (
                      <div className="text-xs text-muted-foreground pl-4">
                        Last: {new Date(app.reminder_stats.whatsapp.dates[app.reminder_stats.whatsapp.dates.length - 1]).toLocaleDateString()}
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> SMS:
                      </span>
                      <span className="font-medium">{app.reminder_stats?.sms?.count || 0}</span>
                    </div>
                    {app.reminder_stats?.sms?.dates?.length > 0 && (
                      <div className="text-xs text-muted-foreground pl-4">
                        Last: {new Date(app.reminder_stats.sms.dates[app.reminder_stats.sms.dates.length - 1]).toLocaleDateString()}
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="flex items-center gap-1">
                        <Bell className="w-3 h-3" /> Automated:
                      </span>
                      <span className="font-medium">{app.reminder_stats?.automated?.count || 0}</span>
                    </div>
                    {app.reminder_stats?.automated?.dates?.length > 0 && (
                      <div className="text-xs text-muted-foreground pl-4">
                        Last: {new Date(app.reminder_stats.automated.dates[app.reminder_stats.automated.dates.length - 1]).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <div className="border-t pt-1 flex justify-between font-medium">
                    <span>Total:</span>
                    <span>{app.reminder_stats?.total || 0}</span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableCell>
      )}
      {isColumnVisible('created_at') && (
        <TableCell className="text-muted-foreground" onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`)}>
          {new Date(app.created_at).toLocaleDateString()}
        </TableCell>
      )}
      {isColumnVisible('app_age') && (
        <TableCell>
          <Badge 
            variant={appAgeDays <= 7 ? "default" : appAgeDays <= 30 ? "secondary" : "destructive"}
            className={`cursor-pointer hover:opacity-80 transition-opacity ${appAgeDays <= 7 ? "bg-green-500/10 text-green-600 border-green-500/20" : appAgeDays <= 30 ? "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" : "bg-red-500/10 text-red-600 border-red-500/20"}`}
            onClick={(e) => fetchDeskHistory(app.id, e)}
            title="Click to view desk shift history"
          >
            <History className="w-3 h-3 mr-1" />
            {appAgeDays} {appAgeDays === 1 ? 'day' : 'days'}
          </Badge>
        </TableCell>
      )}
      {isColumnVisible('offer_age') && (
        <TableCell>
          {offerAgeDays !== null ? (
            <Badge 
              variant={offerAgeDays <= 14 ? "default" : offerAgeDays <= 30 ? "secondary" : "destructive"}
              className={`${offerAgeDays <= 14 ? "bg-blue-500/10 text-blue-600 border-blue-500/20" : offerAgeDays <= 30 ? "bg-orange-500/10 text-orange-600 border-orange-500/20" : "bg-red-500/10 text-red-600 border-red-500/20"}`}
              title={`Offer dated: ${new Date(dateOfOffer).toLocaleDateString()}`}
            >
              <Calendar className="w-3 h-3 mr-1" />
              {offerAgeDays} {offerAgeDays === 1 ? 'day' : 'days'}
            </Badge>
          ) : (
            <span className="text-muted-foreground text-xs">-</span>
          )}
        </TableCell>
      )}
      {isColumnVisible('apply_date') && (
        <TableCell
          onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}&desk=admission`)}
          className="text-xs text-muted-foreground"
          data-testid={`apply-date-${app.id}`}
        >
          {app.desk_data?.admission?.apply_date
            ? new Date(app.desk_data.admission.apply_date).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
            : '-'}
        </TableCell>
      )}
      {isColumnVisible('offer_date') && (
        <TableCell
          onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}&desk=admission`)}
          className="text-xs text-muted-foreground"
          data-testid={`offer-date-${app.id}`}
        >
          {app.desk_data?.admission?.date_of_offer
            ? new Date(app.desk_data.admission.date_of_offer).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
            : '-'}
        </TableCell>
      )}
      {isColumnVisible('actions') && (
        <TableCell onClick={(e) => e.stopPropagation()}
                   className="app-tbl-stick-right1 sticky right-[50px] bg-background z-10 shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.18)]">
          <TooltipProvider>
            <div className="flex items-center gap-0.5">
              {/* Chat Button - Show dropdown for agent-submitted apps */}
              {app.agent_id ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="relative h-7 w-7"
                      data-testid={`chat-btn-${app.id}`}
                      title="Chat"
                    >
                      <MessageCircle className="w-4 h-4" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b mb-1">
                      Chat with
                    </div>
                    <DropdownMenuItem 
                      onClick={() => openAgentChat && openAgentChat(app)}
                      className="cursor-pointer py-2"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-3 h-3 text-blue-600" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">Agent</span>
                          <span className="text-xs text-muted-foreground">{app.transferred_by_agent_name || 'Submitting Agent'}</span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => openChat(app)}
                      className="cursor-pointer py-2"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                          <Users className="w-3 h-3 text-amber-500" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">Team Members</span>
                          <span className="text-xs text-muted-foreground">Internal discussion</span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="relative h-7 w-7"
                  onClick={() => openChat(app)}
                  data-testid={`chat-btn-${app.id}`}
                  title="Chat"
                >
                  <MessageCircle className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              )}
              {/* Reminder Button with Sent (blue) and Pending (red) counters */}
              <div className="relative flex items-center">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className={`h-7 w-7 ${(reminderStats.sent > 0 || reminderStats.pending > 0) ? 'text-slate-700' : ''}`}
                  onClick={() => openReminder(app)}
                  data-testid={`reminder-btn-${app.id}`}
                  title="Reminders"
                >
                <Calendar className="w-4 h-4" />
              </Button>
              {/* Reminder Counters - Blue (sent) and Red (pending) */}
              <div className="flex items-center gap-0.5 ml-[-4px]">
                {reminderStats.sent > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div 
                        className="flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-white text-[9px] font-bold cursor-help"
                        data-testid={`reminder-sent-${app.id}`}
                      >
                        {reminderStats.sent > 9 ? '9+' : reminderStats.sent}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>{reminderStats.sent} reminder(s) sent</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {reminderStats.pending > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div 
                        className="flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold cursor-help"
                        data-testid={`reminder-pending-${app.id}`}
                      >
                        {reminderStats.pending > 9 ? '9+' : reminderStats.pending}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>{reminderStats.pending} reminder(s) due/pending</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
              onClick={(e) => {
                e.stopPropagation();
                if (typeof openWhatsAppCompose === 'function') {
                  openWhatsAppCompose(app, e);
                  return;
                }
                const phone = app.student_phone?.replace(/[^0-9]/g, '');
                if (phone) {
                  window.open(`https://wa.me/${phone}`, '_blank');
                } else {
                  toast.error('No phone number available for this student');
                }
              }}
              data-testid={`whatsapp-btn-${app.id}`}
              title="Send WhatsApp message (via extension)"
            >
              <WhatsAppIcon />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-7 w-7 text-amber-500 hover:text-slate-800 hover:bg-amber-50"
              onClick={(e) => openSMSCompose(app, e)}
              data-testid={`sms-btn-${app.id}`}
              title="Send SMS"
            >
              <MessageSquare className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              onClick={(e) => openEmailCompose(app, e)}
              data-testid={`email-btn-${app.id}`}
              title="Send Email"
            >
              <Mail className="w-4 h-4" />
            </Button>
            <div className="relative">
              <Button 
                variant="ghost" 
                size="icon"
                className={`h-7 w-7 ${app.has_vetting_report ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'}`}
                onClick={(e) => openVettingDialog(app, e)}
                data-testid={`vet-btn-${app.id}`}
                title={app.has_vetting_report ? `Vetted: ${app.vetting_status} - Click to view/re-run` : "AI Document Vetting"}
              >
                <ClipboardCheck className="w-4 h-4" />
              </Button>
              {app.has_vetting_report && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border border-white" title="Has vetting report" />
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-amber-500 hover:text-slate-800 hover:bg-amber-50"
              onClick={(e) => {
                e.stopPropagation();
                if (typeof openContentWriter === 'function') openContentWriter(app, e);
              }}
              data-testid={`content-writer-btn-${app.id}`}
              title="AI Content Writer (SOP / Resume)"
            >
              <PenTool className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-7 w-7 text-slate-600 hover:text-slate-700 hover:bg-slate-50"
              onClick={(e) => {
                e.stopPropagation();
                openActivityLog && openActivityLog(app);
              }}
              data-testid={`activity-btn-${app.id}`}
              title="Activity Log"
            >
              <History className="w-4 h-4" />
            </Button>
            {app.document_upload_token && (
              <Button 
                variant="ghost" 
                size="icon"
                className="h-7 w-7 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                onClick={(e) => {
                  e.stopPropagation();
                  const uploadUrl = `${window.location.origin}/student/documents/${app.document_upload_token}`;
                  navigator.clipboard.writeText(uploadUrl);
                  toast.success('Student upload link copied!');
                }}
                data-testid={`copy-link-btn-${app.id}`}
                title="Copy Student Document Upload Link"
              >
                <Link2 className="w-4 h-4" />
              </Button>
            )}
            {/* r66.8 — Duplicate to another university */}
            {openDuplicateDialog && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-amber-500 hover:text-slate-800 hover:bg-amber-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDuplicateDialog(app);
                    }}
                    data-testid={`duplicate-btn-${app.id}`}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Duplicate to another university</TooltipContent>
              </Tooltip>
            )}
            </div>
          </TooltipProvider>
        </TableCell>
      )}
      {isColumnVisible('more') && (
        <TableCell onClick={(e) => e.stopPropagation()}
                   className="app-tbl-stick-right0 sticky right-0 bg-background z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`app-actions-${app.id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/applications/${app.id}?tab=${currentTab}&page=${currentPage}&limit=${currentLimit}`)}>
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => openAssignDialog(app)}>
                  <UserCircle className="w-4 h-4 mr-2" />
                  Change Case Owner
                </DropdownMenuItem>
              )}
              {openDuplicateDialog && (
                <DropdownMenuItem
                  onClick={() => openDuplicateDialog(app)}
                  className="text-amber-500 focus:text-slate-800 focus:bg-amber-50"
                  data-testid={`duplicate-menu-${app.id}`}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate to another university
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => handleDelete(app.id)} className="text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      )}
    </TableRow>
  );
};

export default ApplicationTableRow;
